import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { orderApi } from "@/features/commerce/api";
import { ROUTES } from "@/constants/routes";

/**
 * PaymentResult — fallback status page for online (Razorpay) payments. We poll the order until the payment reaches a
 * terminal state (paid / failed / cancelled), then push the user to the
 * order-success page (paid) or back to order-detail (failed/pending).
 */
export default function PaymentResult() {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [attempts, setAttempts] = useState(0);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { data: order, refetch } = useQuery({
        queryKey: ["order", id],
        queryFn: () => orderApi.get(id),
        refetchInterval: 3000,
    });

    useEffect(() => {
        if (!order) return;
        const s = order.payment?.status;
        if (s === "paid") {
            if (timer.current) clearTimeout(timer.current);
            navigate(ROUTES.orderSuccessBuild(order.id), { replace: true });
            return;
        }
        // Cap polling at ~2 min so we don't spin forever.
        if (attempts >= 40) {
            navigate(ROUTES.orderDetailBuild(order.id), { replace: true });
            return;
        }
        timer.current = setTimeout(() => {
            setAttempts((n) => n + 1);
            refetch();
        }, 3000);
        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, [order, attempts, navigate, refetch]);

    if (!order) {
        return (
            <div data-testid="payment-result-page" className="tsgo-page">
                <div className="tsgo-container flex min-h-[60vh] flex-col items-center justify-center text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-brand" />
                    <p className="mt-3 text-sm text-muted-foreground">
                        Confirming your payment…
                    </p>
                </div>
            </div>
        );
    }

    const failed = order.payment?.status === "failed";

    return (
        <div data-testid="payment-result-page" className="tsgo-page">
            <div className="tsgo-container flex min-h-[60vh] max-w-lg flex-col items-center justify-center gap-4 text-center">
                {failed ? (
                    <>
                        <XCircle className="h-12 w-12 text-error" />
                        <h1 className="tsgo-h1" data-testid="payment-result-failed">
                            Payment failed
                        </h1>
                        <p className="tsgo-lead">
                            {order.payment?.error ||
                                "Your payment didn't go through. You can retry with the same method or pick a different one."}
                        </p>
                        <Button
                            onClick={() => navigate(ROUTES.orderDetailBuild(order.id))}
                            className="bg-brand text-white hover:bg-brand-600"
                            data-testid="payment-result-view-order"
                        >
                            View order & retry payment
                        </Button>
                    </>
                ) : (
                    <>
                        <Loader2 className="h-10 w-10 animate-spin text-brand" />
                        <h1 className="tsgo-h1">Confirming your payment</h1>
                        <p className="tsgo-lead">
                            Hang on — we're confirming your payment. This usually takes a
                            few seconds.
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Order {order.number} · Attempt {attempts + 1}/40
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
